import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import LessonReviewApiUtils from '@utils/api/lessonReview';
import { useFlashMessage } from '@root/hooks/use-flash-message';
import type { ReviewModalContainerProps } from './review-modal.types';

interface ReviewState {
  totalSatisfaction: number | null;
  audioQuality: number | null;
  reviewText: string;
}

export const useReviewModalService = (props: ReviewModalContainerProps) => {
  const {
    hideModal,
    addModalMessage,
    clearModalMessage,
    lesson,
  } = props;

  const { t } = useTranslation('common');
  const { addFlash } = useFlashMessage();

  const [state, setState] = useState<ReviewState>({
    totalSatisfaction: null,
    audioQuality: null,
    reviewText: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize state from existing review if lesson is already reviewed
  useEffect(() => {
    if (lesson.reviewed && lesson.lesson_review) {
      const { review, total_satisfaction, audio_quality } = lesson.lesson_review;
      setState({
        reviewText: review || '',
        totalSatisfaction: total_satisfaction,
        audioQuality: audio_quality,
      });
    }
  }, [lesson.reviewed, lesson.lesson_review]);

  const handleTotalSatisfactionChange = useCallback(
    (e: React.MouseEvent, rating: number) => {
      e.preventDefault();
      setState((prev) => ({ ...prev, totalSatisfaction: rating }));
    },
    [],
  );

  const handleAudioQualityChange = useCallback(
    (e: React.MouseEvent, rating: number) => {
      e.preventDefault();
      setState((prev) => ({ ...prev, audioQuality: rating }));
    },
    [],
  );

  const handleReviewTextChange = useCallback((value: string) => {
    setState((prev) => ({ ...prev, reviewText: value }));
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent | React.MouseEvent) => {
      e.preventDefault();

      const { audioQuality, totalSatisfaction, reviewText } = state;

      if (!audioQuality || !totalSatisfaction) {
        addModalMessage(t('modals.review.errorMessage'), {
          type: 'error',
          autoDisappear: true,
        });
        return;
      }

      clearModalMessage();
      setIsSubmitting(true);

      try {
        const response = await LessonReviewApiUtils.create(
          lesson.id,
          audioQuality,
          totalSatisfaction,
          reviewText,
        );

        addFlash(response.message, {
          autoDisappear: false,
          sticky: true,
          type: 'success',
        });
        hideModal();
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : t('errors.generic');
        addModalMessage(errorMessage, {
          autoDisappear: true,
          type: 'error',
        });
        setIsSubmitting(false);
      }
    },
    [state, lesson.id, t, addModalMessage, clearModalMessage, addFlash, hideModal],
  );

  const handleCancel = useCallback(() => {
    hideModal();
  }, [hideModal]);

  // Check if lesson is already reviewed
  const isReviewed = lesson.reviewed && lesson.lesson_review;

  return {
    // State
    totalSatisfaction: state.totalSatisfaction,
    audioQuality: state.audioQuality,
    reviewText: state.reviewText,
    isSubmitting,
    isReviewed,
    // Existing review data (for read-only view)
    existingReview: lesson.lesson_review,
    // Handlers
    onTotalSatisfactionChange: handleTotalSatisfactionChange,
    onAudioQualityChange: handleAudioQualityChange,
    onReviewTextChange: handleReviewTextChange,
    onSubmit: handleSubmit,
    onCancel: handleCancel,
    // Pass through modal message props
    message: props.message,
    messageType: props.messageType,
  };
};
